import { createSlice } from '@reduxjs/toolkit';
import { message } from '@docon/docon-ui-library';
import { createAsyncAction } from '../action';
import { Status } from './index';

const getAllCandidates = createAsyncAction('getAllCandidates', 'getAllCandidates');
const getCandidate = createAsyncAction('getCandidate', 'getCandidate');
const getFilteredCandidates = createAsyncAction('getFilteredCandidates', 'getFilteredCandidates');
const downloadAllCandidates = createAsyncAction('downloadAllCandidates', 'downloadAllCandidates');
const downloadCandidateExcel = createAsyncAction('downloadCandidateExcel', 'downloadCandidateExcel');
const uploadCandidateExcel = createAsyncAction('uploadCandidateExcel', 'uploadCandidateExcel');
const addNewCandidates = createAsyncAction('addNewCandidates', 'addNewCandidates');
const editCandidate = createAsyncAction('editCandidate', 'editCandidate');
const updateStatus = createAsyncAction('updateStatus', 'updateStatus');

interface IInitialState {
  isFetchingCandidates: boolean;
  isFetchingCandidate: boolean;
  isFetchingFilteredCandidates: boolean;
  isAddingCandidate: boolean;
  isCandidateAdded: boolean;
  isUpdatingCandidate: boolean;
  uploadingCandidateExcelStatus: string;
  updatingStatus: string;
  fetchingFilteredCandidatesError: string;
  fetchingCandidatesError: string;
  downloadCandidateExcelLink: string;
  downloadAllCandidatesLink: string;
  downloadAllCandidatesStatus: string;
  totalCandidates: number;
  candidatesPerPage: number;
  totalPages: number;
  candidates: any;
  candidateById: any;
}

const initialState: IInitialState = {
  isFetchingCandidates: false,
  isFetchingCandidate: false,
  isFetchingFilteredCandidates: false,
  isAddingCandidate: false,
  isCandidateAdded: false,
  isUpdatingCandidate: false,
  uploadingCandidateExcelStatus: '',
  updatingStatus: '',
  fetchingFilteredCandidatesError: '',
  fetchingCandidatesError: '',
  downloadCandidateExcelLink: '',
  downloadAllCandidatesLink: '',
  downloadAllCandidatesStatus: '',
  candidates: [],
  candidateById: {},
  totalCandidates: 0,
  candidatesPerPage: 20,
  totalPages: 1,
};

export const counterSlice = createSlice({
  name: 'candidate',
  initialState,
  reducers: {
    updateCandidateState: (state, action) => Object.assign(state, action.payload),
  },
  extraReducers: {
    [addNewCandidates.pending as any]: (state) => {
      state.isAddingCandidate = true;
      state.isCandidateAdded = false;
    },
    [addNewCandidates.fulfilled as any]: (state) => {
      state.isAddingCandidate = false;
      state.isCandidateAdded = true;
    },
    [addNewCandidates.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError') {
        state.isAddingCandidate = false;
        state.isCandidateAdded = false;
      }
    },

    [getAllCandidates.pending as any]: (state) => {
      state.isFetchingCandidates = true;
      state.fetchingCandidatesError = '';
    },
    [getAllCandidates.fulfilled as any]: (state, { payload }) => {
      const { data = [], total, total_pages, page_number } = payload?.data || {};
      state.candidates = data || [];
      state.totalCandidates = total;
      state.totalPages = total_pages;
      state.candidatesPerPage = page_number === 1 ? data?.length : state.candidatesPerPage;
      state.isFetchingCandidates = false;
    },
    [getAllCandidates.rejected as any]: (state, { error, payload }) => {
      if (error.name !== 'AbortError') {
        state.fetchingCandidatesError = payload.message;
        state.isFetchingCandidates = false;
      }
    },
    [getCandidate.pending as any]: (state) => {
      state.isFetchingCandidate = true;
    },
    [getCandidate.fulfilled as any]: (state, { meta, payload }) => {
      const candidateId = meta.arg.candidateId;
      state.candidateById[candidateId] = payload.data || {};
      state.isFetchingCandidate = false;
    },
    [getCandidate.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError') {
        state.isFetchingCandidate = false;
      }
    },
    [getFilteredCandidates.pending as any]: (state) => {
      state.isFetchingFilteredCandidates = true;
      state.fetchingFilteredCandidatesError = '';
    },
    [getFilteredCandidates.fulfilled as any]: (state, { payload }) => {
      const { data = [], total, total_pages, page_number } = payload?.data || {};
      state.candidates = data || [];
      state.totalCandidates = total;
      state.totalPages = total_pages;
      state.candidatesPerPage = page_number === 1 ? data?.length : state.candidatesPerPage;
      state.isFetchingFilteredCandidates = false;
    },
    [getFilteredCandidates.rejected as any]: (state, { error, payload }) => {
      if (error.name !== 'AbortError') {
        state.fetchingFilteredCandidatesError = payload.message;
        state.isFetchingFilteredCandidates = false;
      }
    },

    [updateStatus.pending as any]: (state) => {
      state.updatingStatus = Status.pending;
    },
    [updateStatus.fulfilled as any]: (state) => {
      state.updatingStatus = Status.fulfilled;
    },
    [updateStatus.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError') {
        state.updatingStatus = Status.rejected;
      }
    },

    [editCandidate.pending as any]: (state) => {
      state.isUpdatingCandidate = true;
      state.updatingStatus = Status.pending;
    },
    [editCandidate.fulfilled as any]: (state) => {
      state.isUpdatingCandidate = false;
      state.updatingStatus = Status.fulfilled;
    },
    [editCandidate.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError') {
        state.isUpdatingCandidate = false;
        state.updatingStatus = Status.rejected;
      }
    },

    [downloadCandidateExcel.pending as any]: (state) => {
      state.downloadCandidateExcelLink = '';
    },
    [downloadCandidateExcel.fulfilled as any]: (state, { payload }) => {
      state.downloadCandidateExcelLink = window.URL.createObjectURL(new Blob([payload]));
    },

    [downloadAllCandidates.pending as any]: (state) => {
      state.downloadAllCandidatesLink = '';
      state.downloadAllCandidatesStatus = Status.pending;
    },
    [downloadAllCandidates.fulfilled as any]: (state, { payload }) => {
      state.downloadAllCandidatesStatus = Status.fulfilled;
      state.downloadAllCandidatesLink = window.URL.createObjectURL(new Blob([payload], { type: 'application/csv' }));
    },
    [downloadAllCandidates.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError') {
        state.downloadAllCandidatesStatus = Status.rejected;
      }
    },

    [uploadCandidateExcel.pending as any]: (state) => {
      state.uploadingCandidateExcelStatus = Status.pending;
    },
    [uploadCandidateExcel.fulfilled as any]: (state) => {
      state.uploadingCandidateExcelStatus = Status.fulfilled;
      message.success('File upload successfully');
    },
    [uploadCandidateExcel.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError') {
        state.uploadingCandidateExcelStatus = Status.rejected;
      }
    },
  },
});

const { updateCandidateState } = counterSlice.actions;
export {
  updateCandidateState,
  getAllCandidates,
  getCandidate,
  downloadAllCandidates,
  downloadCandidateExcel,
  uploadCandidateExcel,
  getFilteredCandidates,
  addNewCandidates,
  editCandidate,
  updateStatus,
};
export default counterSlice.reducer;
